import { AnimatedSection } from "@/components/AnimatedSection";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTermsCount } from "@/hooks/useTerms";
import {
  BookOpen,
  Check,
  Eye,
  Languages,
  Target,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

function splitBullets(raw: string): string[] {
  return raw
    .split("|")
    .map(s => s.trim())
    .filter(Boolean);
}

function CircularIcon({
  icon: Icon,
  className = "",
}: {
  icon: LucideIcon;
  className?: string;
}) {
  return (
    <div
      className={`mx-auto mb-4 flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full border border-accent/25 bg-gradient-to-br from-white to-[hsl(43,40%,96%)] shadow-[0_4px_20px_-4px_hsl(220_30%_15%_/_0.12)] ${className}`}
    >
      <Icon className="h-8 w-8 text-accent" strokeWidth={1.5} />
    </div>
  );
}

interface PurposeCardProps {
  icon: LucideIcon;
  title: string;
  lead: string;
  bullets: string[];
  delay?: number;
}

function PurposeCard({
  icon: Icon,
  title,
  lead,
  bullets,
  delay,
}: PurposeCardProps) {
  return (
    <AnimatedSection
      delay={delay}
      className="flex h-full min-h-0 flex-col"
    >
      <article className="flex h-full flex-col rounded-[20px] border border-white/80 bg-white/95 p-6 shadow-[var(--shadow-elevated)] backdrop-blur-sm md:p-7 lg:p-8">
        <CircularIcon icon={Icon} />
        <h3 className="mb-2 text-center font-serif text-xl font-bold tracking-tight text-[hsl(220,50%,12%)] md:text-2xl">
          <span className="text-accent">{title}</span>
        </h3>
        {lead.trim() ? (
          <p className="mb-4 text-center text-sm leading-relaxed text-[hsl(220,25%,28%)] md:text-[0.9375rem]">
            {lead}
          </p>
        ) : null}
        <ul
          className={`mt-auto flex flex-1 flex-col gap-2.5 text-left ${lead.trim() ? "" : "mt-2"}`}
        >
          {bullets.map((item, i) => (
            <li
              key={i}
              className="flex gap-2.5 text-sm leading-snug text-[hsl(220,30%,22%)] md:text-[0.9375rem] md:leading-relaxed"
            >
              <span
                className="mt-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent/15"
                aria-hidden
              >
                <Check className="h-2.5 w-2.5 text-accent" strokeWidth={3} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </article>
    </AnimatedSection>
  );
}

interface PurposeStatProps {
  icon: LucideIcon;
  value: string | number;
  label: string;
  delay?: number;
}

function PurposeStat({
  icon: Icon,
  value,
  label,
  delay,
}: PurposeStatProps) {
  return (
    <AnimatedSection delay={delay} className="h-full">
      <div className="flex h-full items-center gap-4 rounded-[20px] border border-white/70 bg-white/90 px-5 py-4 shadow-[var(--shadow-card)] backdrop-blur-sm md:px-6">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-accent/20 bg-gradient-to-br from-accent/10 to-white">
          <Icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-bold tabular-nums leading-none text-[hsl(220,50%,12%)] md:text-3xl">
            {value}
          </p>
          <p className="mt-1 text-sm font-medium text-[hsl(220,20%,32%)]">
            {label}
          </p>
        </div>
      </div>
    </AnimatedSection>
  );
}

export function PurposeSection() {
  const { t } = useLanguage();
  const { data: termsCount, isLoading } = useTermsCount();

  const missionBullets = splitBullets(t("index.missionBullets"));
  const visionBullets = splitBullets(t("index.visionBullets"));

  const termsValue = isLoading
    ? "—"
    : (termsCount ?? 0).toLocaleString();

  return (
    <section className="relative overflow-hidden py-8 md:py-12 lg:py-14">
      {/* Soft gradient backdrop */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            "linear-gradient(165deg, hsl(43 35% 97%) 0%, hsl(0 0% 100%) 35%, hsl(220 28% 97%) 100%)",
        }}
      />

      {/* Jain-inspired dot lattice */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        aria-hidden
        style={{
          backgroundImage: `radial-gradient(circle at center, hsl(43 85% 55% / 0.14) 1px, transparent 1px)`,
          backgroundSize: "28px 28px",
        }}
      />

      {/* Corner saffron glow */}
      <div className="bg-accent/8 pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full blur-3xl" />
      <div className="bg-primary/6 pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full blur-3xl" />

      <div className="container relative">
        <AnimatedSection className="mb-6 text-center md:mb-8">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent md:text-sm">
            {t("index.purposeEyebrow")}
          </p>
          <h2 className="font-serif text-2xl font-bold text-[hsl(220,50%,12%)] md:text-3xl lg:text-4xl">
            {t("index.purposeTitle")}
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-[hsl(220,20%,32%)] md:text-base">
            {t("index.purposeSubtitle")}
          </p>
        </AnimatedSection>

        <div className="grid items-stretch gap-5 md:grid-cols-2 md:gap-6 lg:gap-7">
          <PurposeCard
            icon={Target}
            title={t("index.mission")}
            lead={t("index.missionLead")}
            bullets={missionBullets}
          />
          <PurposeCard
            icon={Eye}
            title={t("index.vision")}
            lead={t("index.visionLead")}
            bullets={visionBullets}
            delay={0.08}
          />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4 md:mt-6">
          <PurposeStat
            icon={BookOpen}
            value={termsValue}
            label={t("index.statTerms")}
          />
          <PurposeStat
            icon={Languages}
            value={t("index.statLanguagesValue")}
            label={t("index.statLanguages")}
            delay={0.05}
          />
          <PurposeStat
            icon={Users}
            value={t("index.statCommunityValue")}
            label={t("index.statCommunity")}
            delay={0.1}
          />
        </div>
      </div>
    </section>
  );
}
